import { createFileRoute } from "@tanstack/react-router";

import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import SINGLE_TAG_CONTEXT from "src/context/internal/tag";
import { Button } from "src/components/base/button";
import { ArrowTopRightOnSquareIcon, EllipsisVerticalIcon, LinkSlashIcon } from "@heroicons/react/20/solid";
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from "src/components/base/dropdown";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { ApiInternal } from "src/api/api_internal";
import { UUID } from "src/api/api_common";

/**
 * The properties for {@link AssociatedDevices}
 */
export type AssociatedDevicesProps = {};

/**
 * A list of all associated devices
 */
function AssociatedDevices(props: AssociatedDevicesProps) {
    const [t] = useTranslation("admin-tag");
    const [tg] = useTranslation();

    const [openUnlinkDevice, setOpenUnlinkDevice] = React.useState<FullDevice>();

    const tagContext = React.useContext(SINGLE_TAG_CONTEXT);

    /**
     * Unlink a device from this tag
     *
     * @param device The device to unlink
     */
    const unlinkDevice = async (device: UUID) => {
        await ApiInternal.devices.removeDeviceTag(device, tagContext.tag.uuid);
        tagContext.reset();
    };

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.device-name")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tagContext.tag.devices.map((device) => (
                        <TableRow key={device.uuid}>
                            <TableCell>{device.dnsname}</TableCell>
                            <TableCell>
                                <Button
                                    plain={true}
                                    href={"/a/devices/$deviceId/general"}
                                    params={{ deviceId: device.uuid }}
                                >
                                    <ArrowTopRightOnSquareIcon />
                                </Button>
                                <Dropdown>
                                    <DropdownButton plain={true}>
                                        <EllipsisVerticalIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownItem onClick={() => setOpenUnlinkDevice(device)}>
                                            <LinkSlashIcon />
                                            <DropdownLabel>{t("button.unlink-device")}</DropdownLabel>
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {openUnlinkDevice && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenUnlinkDevice(undefined)}>
                        <DialogTitle>{t("heading.unlink-device", { device: openUnlinkDevice.dnsname })}</DialogTitle>
                        <DialogDescription>{t("description.unlink-device")}</DialogDescription>
                        <DialogActions>
                            <Button onClick={() => setOpenUnlinkDevice(undefined)}>{tg("button.cancel")}</Button>
                            <Button
                                color={"red"}
                                onClick={async () => {
                                    await unlinkDevice(openUnlinkDevice?.uuid);
                                    setOpenUnlinkDevice(undefined);
                                }}
                            >
                                {t("button.unlink-device")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Suspense>
            )}
        </>
    );
}

export const Route = createFileRoute("/_admin/a/tags/$tagId/_tags/associated")({
    component: AssociatedDevices,
});
